import { useQuery } from '@tanstack/react-query';
import { useId, useState, type ReactElement } from 'react';
import { fetchAccounts } from '../../api';
import { Button } from '../details/Button';
import { connectionErrorMessage } from './error-message';
import { accountsKey } from './keys';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './ConnectionSection.module.css';

export const ACCOUNTS_EMPTY_TEXT =
  'A Meta não informou nenhuma conta para este token. Confira, no usuário do sistema, se as contas do WhatsApp foram atribuídas a ele.';

/**
 * "Contas que o token alcança": quando a Meta responde "não encontrei essa conta", quase sempre o ID está errado (não é o
 * da conta) ou o token não tem acesso. Esta lista vem do próprio token: mostra o ID e o nome de cada conta que ele alcança,
 * para a pessoa copiar o certo para o campo da conexão (vários IDs, separados por vírgula). Só consulta quando a pessoa pede.
 */
export function TokenAccounts(): ReactElement {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: accountsKey,
    queryFn: fetchAccounts,
    enabled: open,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const accounts = query.data?.accounts;

  return (
    <div className={styles.accounts}>
      <Button aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((was) => !was)}>
        {open ? 'Ocultar as contas do token' : 'Ver as contas que o token alcança'}
      </Button>
      <div id={panelId} hidden={!open} className={styles.accountsPanel}>
        {open && query.isPending && <p className={forms.hint}>Consultando a Meta…</p>}
        {open && query.isError && (
          <>
            <p role="alert" className={forms.alert}>
              {connectionErrorMessage(
                query.error,
                'Não foi possível consultar as contas do token.',
              )}
            </p>
            <div className={forms.actions}>
              <Button busy={query.isFetching} onClick={() => void query.refetch()}>
                Tentar de novo
              </Button>
            </div>
          </>
        )}
        {open && accounts && accounts.length === 0 && (
          <p className={forms.hint}>{ACCOUNTS_EMPTY_TEXT}</p>
        )}
        {open && accounts && accounts.length > 0 && (
          <>
            <p className={forms.hint}>
              Copie o ID da conta que você quer usar para o campo de IDs acima (várias contas:
              separe os IDs por vírgula; a primeira é a principal).
            </p>
            <ul aria-label="Contas do token" className={styles.accountList}>
              {accounts.map((account) => (
                <li key={account.id} className={styles.accountRow}>
                  <span dir="auto" className={styles.accountName}>
                    {account.name ?? 'Sem nome'}
                  </span>
                  <code className={styles.accountId}>{account.id}</code>
                  <span className={account.inConnection ? section.badgeOn : section.badgeOff}>
                    {account.inConnection ? 'Na conexão' : 'Fora da conexão'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
